/**
 * RecountNotice — the one line every tab shows while its numbers are being
 * counted again.
 *
 * When the filters change, the values and the charts go away (see
 * `hooks/use-filtered-query.ts`) rather than stand there being the previous
 * selection's answer. Skeletons alone say "something is happening"; this says
 * WHAT is happening, in one sentence, without asking the reader to know the
 * word "query".
 *
 * It takes the queries rather than a boolean so a tab cannot go out of sync
 * with its own widgets: the same `isLoading` that blanks a tile raises this
 * line. That flag is false during a routine background refresh of the same
 * filters — nothing is blanked then, so nothing is announced then either.
 *
 * TWO sentences, because "recounting" is a claim about a previous count and
 * on a cold tab there wasn't one — nothing can come back that was never
 * there. `isPlaceholderData` is what tells the two apart: it is true only
 * when what the query is holding belongs to a DIFFERENT filter set, which is
 * exactly "we counted this before, for something else".
 *
 * The row keeps its height in both states, so raising and dropping the
 * sentence never moves the page under the reader's cursor. Two lines' worth is
 * reserved below `sm`, where the sentence wraps.
 */

import { cn } from '@/lib/utils';

export interface RecountNoticeProps {
  /** The tab's queries. Any one of them still counting raises the line; any
   *  one of them holding another filter set's answer makes it a RE-count. */
  queries: ReadonlyArray<{ isLoading: boolean; isPlaceholderData?: boolean }>;
  className?: string;
}

export function RecountNotice({
  queries,
  className,
}: RecountNoticeProps): React.ReactElement {
  const counting = queries.some((q) => q.isLoading);
  const again = queries.some((q) => q.isPlaceholderData);
  return (
    <p
      data-testid="recount-notice"
      // The region is always mounted so the row keeps its height; what is
      // conditional is the TEXT, which is what makes this work as a live
      // region — inserting the sentence is the announcement, and emptying it
      // announces nothing.
      aria-live="polite"
      className={cn(
        'h-10 sm:h-5 overflow-hidden text-sm text-muted-foreground',
        // Animated TEXT, unlike every other pulse on this dashboard, so it
        // honours the reader's reduced-motion setting — a sentence someone is
        // trying to read is a worse thing to oscillate than a grey block.
        counting ? 'motion-safe:animate-pulse' : 'invisible',
        className,
      )}
    >
      {!counting
        ? null
        : again
          ? 'Recounting — these numbers are being counted again.'
          : 'Counting — these numbers are not ready yet.'}
    </p>
  );
}
